import { useCallback, useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { FilterChipItem, FilterChipList } from '@/components/FilterChipList';
import { appTheme } from '@/components/lib/theme';

const FADE_TIMING = { duration: 150 };

type ChipRow = {
    rowKey: string;
    items: FilterChipItem[];
    selectedId: string;
};

type SubGroupChipsProps = ChipRow & {
    onSelect: (id: string) => void;
};

export const SubGroupChips = ({ rowKey, items, selectedId, onSelect }: SubGroupChipsProps) => {
    const [visibleRow, setVisibleRow] = useState<ChipRow>({ rowKey, items, selectedId });
    const opacity = useSharedValue(1);

    const showRow = useCallback(
        (row: ChipRow) => {
            setVisibleRow(row);
            opacity.value = withTiming(1, FADE_TIMING);
        },
        [opacity]
    );

    // the old row keeps its selection and fades out fully first, so rows with identical chips still read as a change
    useEffect(() => {
        if (rowKey === visibleRow.rowKey) {
            showRow({ rowKey, items, selectedId });
            return;
        }
        opacity.value = withTiming(0, FADE_TIMING, finished => {
            if (finished) {
                runOnJS(showRow)({ rowKey, items, selectedId });
            }
        });
    }, [items, opacity, rowKey, selectedId, showRow, visibleRow.rowKey]);

    const fadeStyle = useAnimatedStyle(() => ({ opacity: opacity.value }));

    return (
        <Animated.View style={fadeStyle}>
            <FilterChipList
                key={visibleRow.rowKey}
                items={visibleRow.items}
                selected={visibleRow.selectedId}
                onSelect={onSelect}
                contentContainerStyle={styles.content}
            />
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    content: {
        paddingHorizontal: appTheme.spacing.s16,
        paddingBottom: appTheme.spacing.s16,
    },
});
